import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  notificationsService,
  NotificationItem,
} from '../services/notificationsService';

const NotificationsPage: React.FC = () => {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await notificationsService.list({ limit: 100, unreadOnly });
      setItems(res.items);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [unreadOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const markRead = async (id: number) => {
    await notificationsService.markRead(id);
    setItems((prev) =>
      prev.map((x) => (x.id === id ? { ...x, read_at: new Date().toISOString() } : x)),
    );
  };

  const markAll = async () => {
    await notificationsService.markAllRead();
    load();
  };

  const remove = async (id: number) => {
    await notificationsService.deleteNotification(id);
    setItems((prev) => prev.filter((x) => x.id !== id));
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Notifications</h1>
          <p className="text-sm text-[var(--color-text-secondary)] mt-1">
            In-app alerts produced by your notification rules.
          </p>
        </div>
        <Link
          to="/notifications/rules"
          className="px-4 py-2 rounded-md text-sm font-medium bg-[var(--color-primary)] text-[var(--color-text-inverse)] hover:opacity-90"
          data-testid="manage-rules-link"
        >
          Manage rules
        </Link>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(e) => setUnreadOnly(e.target.checked)}
            data-testid="filter-unread-only"
          />
          Unread only
        </label>
        <button
          type="button"
          onClick={markAll}
          className="text-sm px-3 py-1 rounded-md border border-[var(--color-border-default)] text-[var(--color-text-secondary)] hover:bg-[var(--color-background-surface)]"
          data-testid="mark-all-read"
        >
          Mark all read
        </button>
      </div>

      {error && (
        <div className="p-3 mb-4 text-sm rounded-md" style={{ backgroundColor: 'var(--color-status-error-light, #fef2f2)', color: 'var(--color-error)' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="p-6 text-sm text-[var(--color-text-secondary)]">Loading…</div>
      ) : items.length === 0 ? (
        <div
          className="p-8 rounded-lg border text-center text-[var(--color-text-secondary)]"
          style={{ borderColor: 'var(--color-border-default)', backgroundColor: 'var(--color-background-card)' }}
        >
          No notifications.
        </div>
      ) : (
        <ul
          className="divide-y rounded-lg border overflow-hidden"
          style={{
            borderColor: 'var(--color-border-default)',
            backgroundColor: 'var(--color-background-card)',
          }}
        >
          {items.map((n) => (
            <li
              key={n.id}
              className="p-4 flex items-start gap-3 hover:bg-[var(--color-background-surface)] transition-colors"
              data-testid={`notification-row-${n.id}`}
            >
              <span
                className="mt-1.5 inline-block w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: n.color }}
                aria-hidden="true"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span
                    className={`text-sm ${
                      n.read_at
                        ? 'text-[var(--color-text-secondary)]'
                        : 'text-[var(--color-text-primary)] font-semibold'
                    }`}
                  >
                    {n.message}
                  </span>
                  <span
                    className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase"
                    style={{
                      backgroundColor: n.color + '22',
                      color: n.color,
                    }}
                  >
                    {n.severity}
                  </span>
                </div>
                <div className="text-xs text-[var(--color-text-muted)]">
                  {n.trigger_event} · {new Date(n.created_at).toLocaleString()}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {!n.read_at && (
                  <button
                    type="button"
                    onClick={() => markRead(n.id)}
                    className="text-xs px-2 py-1 rounded border border-[var(--color-border-default)] hover:bg-[var(--color-background-surface)]"
                    data-testid={`mark-read-${n.id}`}
                  >
                    Mark read
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => remove(n.id)}
                  className="text-xs px-2 py-1 rounded text-[var(--color-error)] hover:bg-[var(--color-status-error-light)]"
                  data-testid={`delete-notification-${n.id}`}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default NotificationsPage;
